import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Info, MoreVertical } from 'lucide-react'
import { cn } from '../utils/cn'

export interface IPVPNavigationProps {
  // Title section
  title?: string
  showTitle?: boolean

  // Info section
  infoText?: string
  infoHighlight?: string
  showInfo?: boolean

  // Location section
  locationLabel?: string
  locationValue?: string
  showLocation?: boolean
  onLocationClick?: () => void

  // Notification section
  notificationCount?: number
  showNotifications?: boolean
  onNotificationClick?: () => void

  // User section
  userName?: string
  userInitials?: string
  userAvatar?: string
  showUser?: boolean
  onUserMenuClick?: () => void
  onSignOut?: () => void

  // Menu section
  showMenu?: boolean
  onMenuClick?: () => void

  // Styling
  className?: string
  only?: boolean
}

export function IPVPNavigation({
  title = 'Assessment',
  showTitle = true,
  infoText = 'This assessment helps you discover your strengths and areas where you can grow. There are no right or wrong answers — ',
  infoHighlight = '',
  showInfo = true,
  userName = 'John Doe',
  userInitials = 'JD',
  userAvatar,
  showUser = true,
  only = false,
  onUserMenuClick,
  onSignOut,
  showMenu = true,
  onMenuClick,
  className,
}: IPVPNavigationProps) {
  return (
    <nav
      style={{ justifyContent: only ? 'end' : 'space-between' }}
      className={cn(
        'flex items-center  gap-4 bg-white px-4 py-3 md:px-6 lg:px-8',
        className
      )}
      role='navigation'
      aria-label='Main navigation'
    >
      {/* Left Section - Title */}
      {showTitle && (
        <div className='flex items-center gap-3'>
          <h1 className='text-2xl font-bold text-gray-900 md:text-3xl lg:text-4xl'>
            {title}
          </h1>
        </div>
      )}

      {/* Center Section - Info Message */}
      {showInfo && (
        <div className='hidden flex-1 items-center gap-3 px-4 lg:flex'>
          <div className='flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-500'>
            <Info className='h-4 w-4 text-white' aria-hidden='true' />
          </div>
          <p className='text-sm text-gray-600'>
            {infoText}
            <span className='font-semibold text-gray-900'>{infoHighlight}</span>
          </p>
        </div>
      )}

      {/* Right Section - Location, Notifications, User */}
      <div className='flex items-center gap-2 md:gap-3 lg:gap-4'>
        {/* User Menu */}
        {showUser && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant='ghost'
                className='flex items-center gap-2 hover:bg-gray-50'
                onClick={onUserMenuClick}
                aria-label={`User menu for ${userName}`}
              >
                <Avatar className='h-10 w-10'>
                  <AvatarImage
                    src={userAvatar || '/placeholder.svg'}
                    alt={userName}
                  />
                  <AvatarFallback className='bg-gray-900 text-sm font-semibold text-white'>
                    {userInitials}
                  </AvatarFallback>
                </Avatar>
                <span className='hidden text-sm font-medium text-gray-900 lg:inline'>
                  {userName}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end' className='w-48'>
              <DropdownMenuItem>Profile</DropdownMenuItem>
              <DropdownMenuItem>Settings</DropdownMenuItem>
              {onSignOut && (
                <DropdownMenuItem onClick={onSignOut}>Sign Out</DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* More Menu */}
        {showMenu && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant='ghost'
                size='icon'
                className='hover:bg-gray-50'
                onClick={onMenuClick}
                aria-label='More options'
              >
                <MoreVertical
                  className='h-5 w-5 text-gray-700'
                  aria-hidden='true'
                />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end' className='w-48'>
              <DropdownMenuItem>Help</DropdownMenuItem>
              <DropdownMenuItem>Feedback</DropdownMenuItem>
              <DropdownMenuItem>About</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </nav>
  )
}
